// externals
import type { FC, FocusEvent, KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

// libraries
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { selectFocusedInput, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { GenericObject } from '@makemydeal/dr-platform-types';

// interfaces/types
import type { ColumnMapping, InputChangeInterstateEventHandler, ManualAccessoryPicklistItem } from '../manualAccessoriesTypes';

// constants/enums
import * as deskingConstants from '../../../constants';
import { getLabelWithRequiredHint } from '../manualAccessoriesUtils';
import type { Accessory } from '@makemydeal/dr-dash-types';
import { getValueFromComboboxChangeEvent } from '../../../utils/getValueFromComboboxChangeEvent';

export type ManualAccessoryNameInputStateProps = {
    disabled: boolean;
    index: number;
    name: string;
    nameColumnMapping: ColumnMapping<string>;
    accessories?: Accessory[];
    onAccessorySelectedFromPickList?: (accessoryName: string) => void;
    onManualEntryStarted?: (accessoryName: string) => void;
    hasError?: boolean;
    errorMessage?: string;
};

export type ManualAccessoryNameInputDispatchProps = {
    handleInputChange: InputChangeInterstateEventHandler;
    handleOnBlur: (fieldName: string) => void;
    handleKeyPress: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>, fieldName: string) => void;
    onTouch?: (touched: boolean) => void;
    shouldTriggerValidation?: boolean;
    isOptionalDeskingMode?: boolean;
};
export type ManualAccessoryNameInputProps = ManualAccessoryNameInputDispatchProps & ManualAccessoryNameInputStateProps;

export const ManualAccessoryNameInput: FC<ManualAccessoryNameInputProps> = ({
    disabled,
    index,
    name,
    nameColumnMapping: { fieldName },
    handleInputChange,
    handleOnBlur,
    handleKeyPress,
    onAccessorySelectedFromPickList,
    hasError,
    errorMessage,
    onTouch,
    shouldTriggerValidation,
    isOptionalDeskingMode
}) => {
    const currentInputValueRef = useRef(name);
    const shouldTriggerAfterManualInputRef = useRef(false);

    const isAccessoriesCatalogEnabled = useSelector(dealerSelectors.isAccessoriesCatalogEnabled);
    const availableManualAccessories = useSelector(
        dealerSelectors.getManualAccessories
    ) as unknown as ManualAccessoryPicklistItem[];
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);

    const shouldShowComboBox = !isAccessoriesCatalogEnabled;

    const validateAndHandleBlur = useCallback(() => {
        handleOnBlur(fieldName);
        shouldTriggerAfterManualInputRef.current = false;
    }, [handleOnBlur]);

    useEffect(() => {
        currentInputValueRef.current = name;
        if (shouldTriggerAfterManualInputRef.current === true) {
            validateAndHandleBlur();
        }
    }, [name]);

    useEffect(() => {
        if (shouldTriggerValidation && !isOptionalDeskingMode) {
            validateAndHandleBlur();
        }
    }, [shouldTriggerValidation]);

    const buildOption = (value: string) => ({ label: value, value });
    const comboBoxOptions: ComboBoxOptions = useMemo(
        () => (availableManualAccessories || []).map((accessory: ManualAccessoryPicklistItem) => buildOption(accessory.name || '')),
        [availableManualAccessories]
    );

    const extractStringValue = (value: string | unknown[] | GenericObject | null | undefined): string => {
        if (typeof value === 'string') {
            return value;
        }
        if (Array.isArray(value)) {
            return '';
        }
        if (typeof value === 'object' && value !== null && 'value' in value) {
            const objValue = (value as GenericObject).value;
            return objValue != null ? String(objValue) : '';
        }
        return '';
    };

    const handleAccessoryNameComboBoxChange = (selectedAccessoryName: string | undefined) => {
        const isEmpty = !selectedAccessoryName || selectedAccessoryName === '';

        if (isEmpty) {
            return;
        }

        const selectedAccessory = availableManualAccessories.find(
            (a: ManualAccessoryPicklistItem) => a.name === selectedAccessoryName
        );

        if (selectedAccessory && onAccessorySelectedFromPickList) {
            const accessoryName = selectedAccessory.name;
            onAccessorySelectedFromPickList(accessoryName);
        }
    };

    if (shouldShowComboBox) {
        const nameValue = extractStringValue(name);
        const values = nameValue ? [buildOption(nameValue)] : [];

        return (
            <div
                className={`text-input-${fieldName} base-accessories-label`}
                onFocus={(e: FocusEvent<HTMLElement>) => {
                    onTouch?.(true);
                    if (isAutoSelectEnabled) selectFocusedInput(e);
                }}
            >
                <ComboBox
                    name={`${fieldName}-${index}`}
                    size="sm"
                    data-testid={`${fieldName}-${index}`}
                    displayLabel={true}
                    label={`${deskingConstants.NAME} (required)`}
                    options={comboBoxOptions}
                    value={values}
                    allowNewEntry
                    onChange={(event) => {
                        if (event.domEvent && 'type' in event.domEvent && event.domEvent.type === 'mouseup') return;
                        const value = getValueFromComboboxChangeEvent(event);
                        handleAccessoryNameComboBoxChange(value);
                        currentInputValueRef.current = value || '';
                    }}
                    onBlur={() => {
                        if (name !== currentInputValueRef.current) {
                            const syntheticEvent = {
                                target: {
                                    value: currentInputValueRef.current
                                }
                            } as InterstateOnChangeEvent<string>;
                            handleInputChange(syntheticEvent, fieldName);
                            shouldTriggerAfterManualInputRef.current = true;
                        } else {
                            validateAndHandleBlur();
                        }
                    }}
                    onInputChange={(event: InterstateOnChangeEvent<string>) => {
                        currentInputValueRef.current = event.target.value;
                    }}
                    errorMessage={errorMessage}
                    hasError={hasError}
                    disabled={disabled}
                />
            </div>
        );
    }

    return (
        <TextInput
            size="sm"
            autoFocus={!name.length}
            displayLabel={true}
            className={`text-input-${fieldName} base-accessories-label`}
            data-testid={`${fieldName}-${index}`}
            label={getLabelWithRequiredHint(deskingConstants.NAME, isOptionalDeskingMode)}
            name={`${fieldName}-${index}`}
            onChange={(e) => {
                handleInputChange(e, fieldName);
            }}
            onFocus={() => {
                onTouch?.(true);
            }}
            onBlur={() => {
                handleOnBlur(fieldName);
            }}
            onKeyDown={(e) => {
                handleKeyPress(e, fieldName);
            }}
            placeholder=""
            value={name}
            required={!isOptionalDeskingMode}
            hasError={hasError}
            errorMessage={errorMessage}
            disabled={disabled}
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
        />
    );
};
